import Segment from '@/app/users/tracking/segment.js'

const mixpanelOnly = {
  integrations: {
    Mixpanel: true,
    All: false,
  },
}

export default {
  supportFormOpened(sessionIdPerTab, supportType, parcelOrigin, parcelDestination, shipmentMethod) {
    Segment.track(
      'DSF: Form Opened',
      {
        'Session Id Per Tab': sessionIdPerTab,
        'Type of support request': supportType,
        'Parcel origin': parcelOrigin,
        'Parcel destination': parcelDestination,
        'Shipment method': shipmentMethod,
      },
      mixpanelOnly,
    )
  },

  /**
   * Tracks the outcome of the 'Find my shipment' button for parcel investigations
   *
   * @param {string} sessionIdPerTab
   * @param {string} supportType
   * @param {string} parcelOrigin
   * @param {string} parcelDestination
   * @param {string} shipmentMethod
   * @param {boolean} isDeflected
   * @param {string} deflectionCode
   */
  supportFormDynamicDeflection(
    sessionIdPerTab,
    supportType,
    parcelOrigin,
    parcelDestination,
    shipmentMethod,
    isDeflected,
    deflectionCode = null,
  ) {
    Segment.track('DSF: Dynamic Deflection', {
      'Session Id Per Tab': sessionIdPerTab,
      'Type of support request': supportType,
      'Parcel origin': parcelOrigin,
      'Parcel destination': parcelDestination,
      'Shipment method': shipmentMethod,
      'Deflected': isDeflected,
      'Deflection Code': deflectionCode,
    })
  },

  supportFormSubmitted(
    sessionIdPerTab,
    supportType,
    parcelOrigin,
    parcelDestination,
    shipmentMethod,
    smartShippingData,
  ) {
    Segment.track(
      'DSF: Form Submitted',
      {
        'Session Id Per Tab': sessionIdPerTab,
        'Type of support request': supportType,
        'Parcel origin': parcelOrigin,
        'Parcel destination': parcelDestination,
        'Shipment method': shipmentMethod,
        ...smartShippingData,
      },
      mixpanelOnly,
    )
  },

  supportFormErrors(sessionIdPerTab, supportType, errorCode, errorMessages, precedingAction) {
    Segment.track(
      'DSF: Form Errors',
      {
        'Session Id Per Tab': sessionIdPerTab,
        'Type of support request': supportType,
        'Error code': errorCode,
        'Error messages': errorMessages,
        'Error happened while': precedingAction,
      },
      mixpanelOnly,
    )
  },

  supportFormFallbackSubmitted(sessionIdPerTab, supportType) {
    Segment.track(
      'DSF: Fallback Submitted',
      {
        'Session Id Per Tab': sessionIdPerTab,
        'Type of support request': supportType,
      },
      mixpanelOnly,
    )
  },

  helpCenterSuggestionClicked(helpCenterLink) {
    Segment.track(
      'DSF: Clicked on Help Center link',
      {
        'Help Center link': helpCenterLink,
      },
      mixpanelOnly,
    )
  },

  proofOfDeliveriesShown(trackingNumber) {
    Segment.track(
      'Proof of Deliveries: Download link shown',
      {
        'Tracking Number': trackingNumber,
      },
      mixpanelOnly,
    )
  },

  proofOfDeliveriesDownloaded(trackingNumber) {
    Segment.track(
      'Proof of Deliveries: Downloaded',
      {
        'Tracking Number': trackingNumber,
      },
      mixpanelOnly,
    )
  },

  proofOfDeliveriesError(trackingNumber) {
    Segment.track(
      'Proof of Deliveries: Error',
      {
        'Tracking Number': trackingNumber,
      },
      mixpanelOnly,
    )
  },

  covergeniusAlertShown() {
    Segment.track('DSF: Covergenius Alert shown')
  },

  covergeniusAlertClicked() {
    Segment.track('DSF: Covergenius Alert clicked')
  },

  goToNewTicket() {
    Segment.track('Support Portal - Create new ticket', {}, mixpanelOnly)
  },

  /**
   * Tracks when an user clicks on one of the support cards
   *
   * @param {string} subject
   */
  goToSupport(subject) {
    Segment.track(
      'Support - Card Clicked',
      {
        'Card Id': subject,
      },
      mixpanelOnly,
    )
  },

  /**
   * Tracks when an user clicks on the drafts tab
   */
  openDrafts() {
    Segment.track('Support - Drafts Tickets Tab Opened', {}, mixpanelOnly)
  },

  /**
   * Tracks when an user saves a draft
   * @param {string} topic
   */
  saveDraft(topic) {
    Segment.track(
      'Support - Draft Ticket Saved',
      {
        Topic: topic,
      },
      mixpanelOnly,
    )
  },

  /**
   * Tracks when an user leaves the create / edit ticket form without saving a draft
   */
  ticketAbandoned() {
    Segment.track('Support - Ticket abandoned', {}, mixpanelOnly)
  },

  /**
   * Tracks opening the ticket detail view
   * @param {number} id
   */

  openedTicketDetails(id) {
    Segment.track(
      'Ticket details - Opened ticket',
      {
        'Ticket Id': id,
      },
      mixpanelOnly,
    )
  },

  /**
   * Tracks sending a ticket comment in ticket detail view
   * @param {String} id
   * @param {Boolean} hasAttachments
   */
  addedReply(id, hasAttachments) {
    Segment.track(
      'Ticket details - Added reply',
      {
        'Ticket Id': id,
        'Has attachments': hasAttachments,
      },
      mixpanelOnly,
    )
  },

  /**
   * Tracks creating a follow up ticket which happens when a user wants to send a reply to a closed ticket.
   * @param {String} id
   */
  createdFollowUpTicket(id) {
    Segment.track(
      'Ticket details - Created a follow up ticket',
      {
        'Ticket Id': id,
      },
      mixpanelOnly,
    )
  },

  /**
   * Tracks specific events in the DynamicAlert component
   * @param {string} description - name of the event to log
   * @param {number} user_id
   * @param {string} tracking_code
   * @param {string} form_id
   */
  trackAlertEvent(description, user_id, tracking_code, form_id) {
    Segment.track(
      `Support - ${description}`,
      {
        'User ID': user_id,
        'Tracking Code': tracking_code,
        'Form ID': form_id,
      },
      mixpanelOnly,
    )
  },

  /**
   * Tracks granting or revoking agent access
   * @param {string} action
   * @param {string} origin
   */
  trackAgentLoginAccessChange(action, origin) {
    Segment.track(
      `Support - Agent Login Permission Change`,
      {
        Action: action,
        Origin: origin,
      },
      mixpanelOnly,
    )
  },

  /**
   * Survey on why the user decided to wait on a claim
   * @param {number} answerId - id
   * @param {string} answerDescription - answer description
   * @param {string} [answerComment] - (optional) user comment if they choose option 'Other'
   */
  trackWaitSurvey(answerId, answerDescription, answerComment) {
    Segment.track(
      'Support - Wait claim survey',
      {
        'Answer ID': answerId,
        'Answer description': answerDescription,
        'Answer comment': answerComment,
      },
      mixpanelOnly,
    )
  },

  /**
   * Tracks when the user acts on a claim
   * @param {string} action - action (accept or wait)
   * @param {string} source - email or support page
   */
  trackClaimAction(action, source) {
    Segment.track(
      'Support - Action on claim',
      {
        Action: action,
        Source: source,
      },
      mixpanelOnly,
    )
  },

  /**
   * Tracks when the user marks a ticket as solved
   * @param {string} source - ticket-list or detail-view
   */
  trackMarkTicketAsSolved(source) {
    Segment.track(
      'Support - Mark ticket as solved',
      {
        Source: source,
      },
      mixpanelOnly,
    )
  },
}
